// src/components/Step1.jsx
import React, { useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import TarifOption from '../TarifOption';
import { PrivateAxios } from '../../../request/axios.js'
import { toast } from 'react-toastify';
import { sanitizeInput } from '../ContratFunctions.js'


const Step1 = ({ formData, setFormData, handleChange, handleStepChange, handleResetRemise, handleResetGenre, showErrorFormulaire }) => {

    const [userRoles, setUserRoles] = useState([]);
    const navigate = useNavigate();
    const optionPrice = TarifOption;
    const [genres, setGenres] = useState([]);

    const hasRole = (requiredRoles) => {
        return requiredRoles.some(role => userRoles.includes(role));
    };


    // Vérification de la validité des données avant de passer à l'étape suivante
    const validateForm = () => {
        // Sanitize toutes les données du formulaire
        const sanitizedFields = {
            genre: sanitizeInput(formData.genre),
            nom: sanitizeInput(formData.nom),
            nomEpouse: sanitizeInput(formData.nomEpouse),
            prenom: sanitizeInput(formData.prenom),
            adresse: sanitizeInput(formData.adresse),
            numCI: sanitizeInput(formData.numCI),
            codePostal: sanitizeInput(formData.codePostal),
            ville: sanitizeInput(formData.ville),
            telephone: sanitizeInput(formData.telephone),
            destination: sanitizeInput(formData.destination),
            dateDepart: sanitizeInput(formData.dateDepart),
            dateRetour: sanitizeInput(formData.dateRetour),
            profession: sanitizeInput(formData.profession),
            remise: parseFloat(sanitizeInput(formData.remise)) || 0,
            remisePartenaire: parseFloat(sanitizeInput(formData.remisePartenaire)) || 0,
            remiseExceptionnelle: parseFloat(sanitizeInput(formData.remiseExceptionnelle)) || 0,
            optionFamille: formData.optionFamille, // Les valeurs booléennes restent intactes
            optionForfait: formData.optionForfait  // Les valeurs booléennes restent intactes
        };

        // Fusionne les champs sanitizés avec l'état actuel du formData
        setFormData((prevFormData) => ({
            ...prevFormData, // Conserve tous les autres champs intacts
            ...sanitizedFields // Remplace uniquement les champs explicitement sanitizés
        }));

        const {
            genre,
            nom,
            prenom,
            adresse,
            numCI,
            codePostal,
            ville,
            telephone,
            destination,
            dateDepart,
            dateRetour,
            profession,
            remise,
            remisePartenaire,
            remiseExceptionnelle,
            optionFamille
        } = sanitizedFields;

        const today = new Date().toISOString().split('T')[0];

        // Vérification des champs obligatoires
        if (!genre || !nom || !adresse || !codePostal || !ville || !telephone || !destination || !profession || !numCI) {
            showErrorFormulaire('Merci de remplir tous les champs obligatoires.');
            return false;
        }

        if (optionFamille && !prenom) {
            showErrorFormulaire('Merci de remplir tous les champs obligatoires.');
            return false;
        }

        // Vérification de la date de départ
        if (dateDepart < today) {
            showErrorFormulaire('Merci de renseigner une date de départ valide.');
            return false;
        }

        // Vérification de la date de retour par rapport à la date de départ
        if (dateRetour <= dateDepart) {
            showErrorFormulaire('Merci de renseigner une date de retour valide.');
            return false;
        }

        // Vérification si la date de retour est supérieure à 180 jours après la date de départ
        const departDate = new Date(dateDepart);
        const maxReturnDate = new Date(departDate);
        maxReturnDate.setDate(departDate.getDate() + 180);

        if (new Date(dateRetour) > maxReturnDate) {
            showErrorFormulaire('La date de retour ne peut pas dépasser 180 jours après la date de départ.');
            return false;
        }

        if (optionFamille) {
            if (remise < 0 || remise > 30) {
                showErrorFormulaire('La remise ne peut pas dépasser 30% ou être inférieure à 0%');
                return false;
            }

            if (remisePartenaire < 0 || remisePartenaire > 10) {
                showErrorFormulaire('La remise partenaire ne peut pas dépasser 10% ou être inférieure à 0%');
                return false;
            }

            if (remiseExceptionnelle < 0 || remiseExceptionnelle > 20) {
                showErrorFormulaire('La remise exceptionnelle ne peut pas dépasser 20% ou être inférieure à 0%');
                return false;
            }

            if (
                (remise > 0 && remisePartenaire > 0) ||
                (remise > 0 && remiseExceptionnelle > 0) ||
                (remisePartenaire > 0 && remiseExceptionnelle > 0)
            ) {
                showErrorFormulaire('Une seule remise peut être définie');
                return false;
            }
        } else {
            handleResetRemise(); // Réinitialise les remises si l'option famille n'est pas active
        }

        return true;
    };


    const handleNext = () => {
        if (validateForm()) {
            handleStepChange(2);
        }
    };

    const handleDateChange = (e) => {
        let value = e.target.value;

        // Vérifier si l'année contient plus de 4 chiffres
        const [year, month, day] = value.split('-');

        if (year.length > 4) {
            // Couper l'année à 4 chiffres
            value = `${year.slice(0, 4)}-${month}-${day}`;
        }

        e.target.value = value

        // Mettre à jour la date correspondante
        handleChange(e)
    };

    useEffect(() => {
        const token = localStorage.getItem('assistance-axa-token');
        if (token) {
            const decoded = jwtDecode(token);
            setUserRoles(decoded.roles || []);
        }
    }, [navigate]);

    useEffect(() => {
        const getGenre = async () => {
            try {
                const groupe_or_famille = formData.optionFamille ? "famille" : "groupe";
                const response = await PrivateAxios.get("/contrat/genre", {
                    params: { tarification_type: groupe_or_famille } // Remplacez 'groupe' par 'famille' si nécessaire
                });
                setGenres(response.data.genres);
            } catch (error) {
                toast.error('Erreur lors de la récupération des genres...')
            }
        };
        handleResetGenre();
        getGenre();
    }, [formData.optionFamille]);

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <h2 className="mb-4 text-2xl font-bold text-axa-blue-400">Informations Souscripteur</h2>
            <div className="p-4 mb-4 text-orange-700 bg-orange-100 rounded-lg">
                Pour l'option "forfait hospitalier", le calcul de l'éligibilité se base sur la date du retour (pensez à informer l'assuré).
            </div>
            <form>
                {/* --- Sélection du mode de tarification --- */}
                <div className="p-4 mb-4 border shadow-sm rounded-xl bg-gray-50">
                    <h3 className="mb-4 text-xl font-semibold text-axa-blue-400">
                        Tarification & options
                    </h3>

                    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                        {/* Switch pour le mode Individuel / Famille */}
                        <div className="flex items-center justify-between w-full md:w-auto">
                            <span className={`font-medium ${!formData.optionFamille ? 'text-axa-blue-400' : 'text-gray-500'}`}>
                                Groupe
                            </span>

                            <label className="relative inline-flex items-center mx-3 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="optionFamille"
                                    checked={formData.optionFamille}
                                    onChange={handleChange}
                                    className="sr-only peer"
                                />
                                <div className="transition-colors duration-300 bg-gray-200 border border-black rounded-full w-14 h-7 peer-focus:outline-none peer-checked:bg-axa-blue-400 peer-checked:border-none"></div>
                                {/* Thumb */}
                                <div className="absolute w-5 h-5 transition-transform duration-300 rounded-full shadow left-1 top-1 bg-axa-blue-400 peer-checked:bg-white peer-checked:translate-x-7"></div>
                            </label>

                            <span className={`font-medium ${formData.optionFamille ? 'text-axa-blue-400' : 'text-gray-500'}`}>
                                Solo/Duo/Famille
                            </span>
                        </div>

                        {/* Option Forfait Hospitalier */}
                        <div className="flex items-center justify-between w-full md:w-auto">
                            <label className="flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="optionForfait"
                                    checked={formData.optionForfait}
                                    onChange={handleChange}
                                    className="w-5 h-5 mr-2 accent-axa-blue-400"
                                />
                                <span className="font-medium text-gray-700">
                                    Forfait hospitalier
                                    <span className="ml-1 text-sm text-gray-500">
                                        ({optionPrice} € HT / personne éligible)
                                    </span>
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 md:grid-cols-4">
                    <div className='relative flex h-12 w-60 rounded-xl'>
                        <select
                            required
                            id="genre"
                            name="genre"
                            value={formData.genre}
                            onChange={handleChange}
                            className="w-full px-4 text-base bg-transparent bg-white border border-gray-200 outline-none peer rounded-xl focus:ring-axa-blue-400 focus:border-axa-blue-400"
                        >
                            <option value="" disabled hidden></option>
                            {genres.map((genre, index) => (
                                <option key={index} value={genre.value}>{genre.label}</option>
                            ))}
                        </select>
                        <label
                            className="absolute text-gray-700 top-1/2 translate-y-[-50%] bg-white left-4 px-2 peer-focus:top-0 peer-focus:left-3 font-semibold text-base peer-focus:text-sm peer-focus:text-axa-blue-400 peer-valid:-top-0 peer-valid:left-3 peer-valid:text-sm peer-valid:text-axa-blue-400 duration-150"
                            htmlFor="genre"
                        >
                            Genre*
                        </label>
                    </div>
                    {[
                        { label: 'Nom*', name: 'nom', type: 'text' },
                        { label: "Nom d'épouse", name: 'nomEpouse', type: 'text' },
                        { label: 'Prénom*', name: 'prenom', type: 'text' },
                        { label: 'Adresse*', name: 'adresse', type: 'text' },
                        { label: 'Numéro passeport / CI*', name: 'numCI', type: 'text' },
                        { label: 'Profession*', name: 'profession', type: 'text' },
                        { label: 'Code Postal*', name: 'codePostal', type: 'number' },
                        { label: 'Ville*', name: 'ville', type: 'text' },
                        { label: 'Téléphone*', name: 'telephone', type: 'number' },
                        { label: 'Destination*', name: 'destination', type: 'text' },
                        ...(hasRole(['Remise']) ? [{ label: 'Remise salarié', name: 'remise', type: 'number' }] : []),
                        ...(hasRole(['Remise_PART']) ? [{ label: 'Remise partenaire', name: 'remisePartenaire', type: 'number' }] : []),
                        ...(hasRole(['Remise_EXCEPT']) ? [{ label: 'Remise exceptionnelle', name: 'remiseExceptionnelle', type: 'number' }] : [])
                    ].map((field, index) => (
                        <div className="relative flex h-12 w-60 rounded-xl" key={index}>
                            <input
                                className={`peer w-full bg-transparent outline-none px-4 text-base rounded-xl bg-white border border-gray-200 focus:ring-axa-blue-400 focus:border-axa-blue-400 ${['remise', 'remisePartenaire', 'remiseExceptionnelle'].includes(field.name) && 'disabled:bg-slate-100'}`}
                                required={field.name !== 'nomEpouse'}
                                id={field.name}
                                name={field.name}
                                type={field.type}
                                value={!['remise', 'remisePartenaire', 'remiseExceptionnelle'].includes(field.name) ? formData[field.name] : !formData.optionFamille ? '' : formData[field.name]}
                                onChange={handleChange}
                                min="0"
                                max={
                                    field.name === 'remise'
                                        ? 30
                                        : field.name === 'remiseExceptionnelle'
                                            ? 20
                                            : field.name === 'remisePartenaire'
                                                ? 10
                                                : undefined
                                }
                                disabled={['remise', 'remisePartenaire', 'remiseExceptionnelle'].includes(field.name) ? !formData.optionFamille : false}
                            />
                            {field.name === 'remise' && formData.optionFamille && formData.remise !== null && formData.remise !== '' && (
                                <span className="absolute inset-y-0 flex items-center text-gray-700 left-14">%</span>
                            )}
                            {field.name === 'remisePartenaire' && formData.optionFamille && formData.remisePartenaire !== null && formData.remisePartenaire !== '' && (
                                <span className="absolute inset-y-0 flex items-center text-gray-700 left-14">%</span>
                            )}
                            {field.name === 'remiseExceptionnelle' && formData.optionFamille && formData.remiseExceptionnelle !== null && formData.remiseExceptionnelle !== '' && (
                                <span className="absolute inset-y-0 flex items-center text-gray-700 left-14">%</span>
                            )}
                            <label
                                className={`absolute text-gray-700 top-1/2 translate-y-[-50%] left-4 px-2 peer-focus:top-0 peer-focus:left-3 font-semibold text-base peer-focus:text-sm peer-focus:text-axa-blue-400 peer-valid:-top-0 peer-valid:left-3 peer-valid:text-sm peer-valid:text-axa-blue-400 duration-150 ${!['remise', 'remisePartenaire', 'remiseExceptionnelle'].includes(field.name) ? 'bg-white' : formData.optionFamille ? 'bg-white' : 'disabled:bg-slate-300'}`}
                                htmlFor={field.name}
                            >
                                {field.label}
                            </label>
                        </div>
                    ))}
                    <div className='relative flex h-12 w-60 rounded-xl'>
                        <input
                            required={true}
                            id="dateDepart"
                            type="date"
                            name="dateDepart"
                            value={formData.dateDepart}
                            onChange={handleDateChange}
                            className="w-full px-4 text-base bg-transparent bg-white border border-gray-200 outline-none peer rounded-xl focus:ring-axa-blue-400 focus:border-axa-blue-400"
                        />
                        <label
                            className="absolute text-gray-700 top-1/2 translate-y-[-50%] bg-white left-4 px-2 peer-focus:top-0 peer-focus:left-3 font-semibold text-base peer-focus:text-sm peer-focus:text-axa-blue-400 peer-valid:-top-0 peer-valid:left-3 peer-valid:text-sm peer-valid:text-axa-blue-400 duration-150"
                            htmlFor='dateDepart'
                        >
                            Date de Départ*
                        </label>
                    </div>
                    <div className='relative flex h-12 w-60 rounded-xl'>
                        <input
                            required={true}
                            id="dateRetour"
                            type="date"
                            name="dateRetour"
                            value={formData.dateRetour}
                            onChange={handleDateChange}
                            className="w-full px-4 text-base bg-transparent bg-white border border-gray-200 outline-none peer rounded-xl focus:ring-axa-blue-400 focus:border-axa-blue-400"
                        />
                        <label
                            className="absolute text-gray-700 top-1/2 translate-y-[-50%] bg-white left-4 px-2 peer-focus:top-0 peer-focus:left-3 font-semibold text-base peer-focus:text-sm peer-focus:text-axa-blue-400 peer-valid:-top-0 peer-valid:left-3 peer-valid:text-sm peer-valid:text-axa-blue-400 duration-150"
                            htmlFor='dateRetour'
                        >
                            Date de Retour*
                        </label>
                    </div>
                </div>
            </form>
            <div className="flex justify-end mt-6 space-x-4">
                <button
                    type="button"
                    onClick={handleNext}
                    className="px-4 py-2 text-white transition duration-300 rounded-lg bg-axa-blue-400 hover:bg-axa-blue-700"
                >
                    Suivant
                </button>
            </div>
        </div>
    );
};

export default Step1;
